(() => {
  if (customElements.get('wine-atlas')) return;

  class WineAtlas extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.copy = this.readJson('[data-atlas-copy]') || {};
      this.products = this.readJson('[data-atlas-products]');
      this.wineries = this.readJson('[data-atlas-wineries]');
      this.regions = this.readJson('[data-atlas-regions]').map((region) => this.normalizeRegion(region));
      this.regionById = new Map(this.regions.map((region) => [region.id, region]));
      this.regionByCode = new Map(this.regions.map((region) => [String(region.prefCode).padStart(2, '0'), region]));
      this.curatedIds = new Set(this.regions.map((region) => region.id));
      this.fallbackRegions = new Map();
      this.mode = 'shop';
      this.selectedId = '';
      this.compareIds = [];
      this.view = { scale: 1, x: 0, y: 0 };
      this.mapModel = ['isometric-a-2-1', 'isometric-a-2', 'isometric-a', 'regionenatlas-v4', 'fixed-v3', 'classic'].includes(this.dataset.mapModel)
        ? this.dataset.mapModel
        : 'isometric-a-2-1';
      this.useFixedLevels = ['isometric-a-2-1', 'isometric-a-2', 'isometric-a'].includes(this.mapModel) || this.dataset.fixedLevels !== 'false';
      this.mapLevel = 'japan';
      this.geoFeatures = [];
      this.resizeFrame = null;
      this.filters = {};
      this.drag = null;
      this.classList.toggle('uses-fixed-levels', this.useFixedLevels);

      this.mapElement = this.querySelector('[data-atlas-map]');
      this.mapCanvas = this.querySelector('[data-atlas-map-canvas]');
      this.mapStage = this.querySelector('[data-atlas-map-stage]');
      this.tooltip = this.querySelector('[data-atlas-tooltip]');
      this.status = this.querySelector('[data-atlas-status]');
      this.workspace = this.querySelector('.wine-atlas__workspace');
      this.detail = this.querySelector('[data-atlas-detail]');
      this.detailContent = this.querySelector('[data-atlas-detail-content]');
      this.compare = this.querySelector('[data-atlas-compare]');
      this.resultCount = this.querySelector('[data-atlas-result-count]');
      this.search = this.querySelector('[data-atlas-search]');
      this.availableOnly = this.querySelector('[data-atlas-available-only]');
      this.filterPanel = this.querySelector('[data-atlas-filters-panel]');
      this.filterBackdrop = this.querySelector('[data-atlas-filter-backdrop]');
      this.filterToggle = this.querySelector('[data-atlas-filter-toggle]');
      this.filterCount = this.querySelector('[data-atlas-filter-count]');
      this.filterClose = this.querySelector('[data-atlas-close-filters]');
      this.cityToggle = this.querySelector('[data-atlas-city-toggle]');
      this.showCities = this.readCityPreference();
      this.syncCityToggle();
      this.mobileExperience = window.NipponVinoWineAtlasMobile
        ? new window.NipponVinoWineAtlasMobile(this)
        : null;
      this.mobileExperience?.mount();

      this.updateShopRegionCount();
      this.populateFilters();
      this.bindEvents();
      const initialState = this.getInitialState();
      this.initialRegionId = initialState.region;
      this.initialLevel = initialState.level;
      this.mapLevel = initialState.level;
      this.mode = initialState.mode;
      this.search.value = initialState.query;
      this.syncModeButtons();
      this.syncLevelButtons();
      this.applyFilters(false);
      this.loadMap();
    }

    disconnectedCallback() {
      this.mapResizeObserver?.disconnect();
      this.renderer?.destroy?.();
      this.mobileExperience?.destroy?.();
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
    }

    readJson(selector) {
      const element = this.querySelector(selector);
      if (!element) return [];
      try {
        return JSON.parse(element.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    normalizeRegion(region) {
      const cleanList = (list) => (Array.isArray(list) ? list : String(list || '').split(','))
        .map((item) => String(item).trim())
        .filter(Boolean);

      const atlas = this.localizeAtlas(window.NipponVinoWineAtlasRegionData?.[String(region.id || '').trim()] || {});

      const id = String(region.id || '').trim();
      const configuredProducts = this.products.filter((product) => product.primaryRegion === id);
      // Keep the known-good page template compatible while structured product
      // relations are being added. The template's curated count remains the
      // source of truth until a product carries primaryRegion data.
      const staticAvailableWines = this.toOptionalNumber(region.availableWines) || 0;
      const fallbackProducts = this.products
        .filter((product) => id === 'nagano'
          ? /merlot/i.test(String(product.handle || product.title || ''))
          : !/merlot/i.test(String(product.handle || product.title || '')))
        .slice(0, staticAvailableWines);
      const originProducts = configuredProducts.length ? configuredProducts : fallbackProducts;
      const localWineries = this.wineries.filter((winery) => winery.region === id);

      return {
        ...region,
        id,
        prefCode: String(region.prefCode || '').padStart(2, '0'),
        grapes: cleanList(region.grapes),
        styles: cleanList(region.styles),
        wineryCount: this.toOptionalNumber(region.wineryCount),
        // Availability is deliberately based on the wine's primary grape/wine origin,
        // not the location of the winery or the product's GI.
        availableWineries: localWineries.length,
        availableWines: configuredProducts.length ? configuredProducts.length : staticAvailableWines,
        originProducts,
        localWineries,
        atlas,
      };
    }

    updateShopRegionCount() {
      const count = this.regions.filter((region) => region.availableWines > 0).length;
      this.querySelectorAll('[data-atlas-shop-count]').forEach((element) => {
        element.textContent = String(count);
      });
    }

    localizeAtlas(atlas) {
      if (this.dataset.locale !== 'en' || !atlas) return atlas;
      const localizeName = (name) => String(name || '').replaceAll('Tokio', 'Tokyo');
      const localizePoint = (point) => point ? { ...point, name: localizeName(point.name) } : point;
      return {
        ...atlas,
        capital: localizePoint(atlas.capital),
        referenceCity: localizePoint(atlas.referenceCity),
        wineHub: atlas.wineHub ? { ...atlas.wineHub, name: localizeName(atlas.wineHub.name) } : atlas.wineHub,
        additionalPoints: (atlas.additionalPoints || []).map(localizePoint),
      };
    }

    t(key, values = {}) {
      // Locale values may arrive HTML-escaped from Shopify's translation
      // editor. Decode the common ampersand entity before escaping the label
      // for insertion into the rendered tab/button markup.
      let value = String(this.copy?.[key] || key)
        .replaceAll('&amp;', '&')
        .replaceAll('&#38;', '&');
      Object.entries(values).forEach(([name, replacement]) => {
        value = value.replaceAll(`{{ ${name} }}`, String(replacement));
      });
      return value;
    }

    wineCount(count) {
      return this.t(count === 1 ? 'one_wine' : 'other_wines', { count });
    }

    toOptionalNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    formatWineStart(value) {
      const match = String(value || '').match(/\b(1[6-9]\d{2}|20\d{2})\b/);
      return match?.[0] || '–';
    }

    isVerifiedContent(value) {
      const content = String(value || '').trim();
      if (!content) return false;
      return !/^(?:nicht|noch nicht|k\.?\s*a\.?|unbekannt)/i.test(content)
        && !/(?:quellenprüfung|redaktionell(?:e|er|en)?\s+(?:prüfung|erschließung)|wird später ergänzt)/i.test(content);
    }

    displayContent(value) {
      return this.isVerifiedContent(value) ? value : '–';
    }

    getInitialState() {
      try {
        const params = new URLSearchParams(window.location.search);
        const region = params.get('atlas-region') || '';
        const requestedLevel = params.get('level');
        const validLevel = ['japan', 'region', 'detail'].includes(requestedLevel) ? requestedLevel : '';
        const legacyZoom = Number.parseFloat(
          params.get('atlas-zoom') || params.get('atlas-scale') || params.get('zoom') || params.get('scale') || ''
        );
        let level = validLevel;
        if (!level && Number.isFinite(legacyZoom)) {
          level = legacyZoom >= 1.8 ? 'detail' : legacyZoom > 1.08 ? 'region' : 'japan';
        }
        if (!region || !['region', 'detail'].includes(level)) level = 'japan';
        return {
          region,
          mode: params.get('atlas-mode') === 'all' ? 'all' : 'shop',
          query: params.get('atlas-q') || '',
          level,
        };
      } catch (error) {
        return { region: '', mode: 'shop', query: '', level: 'japan' };
      }
    }

    populateFilters() {
      const options = {
        prefecture: this.regions.map((region) => region.prefecture),
        climate: this.regions.map((region) => region.climate),
        soil: this.regions.map((region) => region.soil),
        grapes: this.regions.flatMap((region) => region.grapes),
        styles: this.regions.flatMap((region) => region.styles),
      };

      Object.entries(options).forEach(([key, values]) => {
        const select = this.querySelector(`[data-atlas-filter="${key}"]`);
        if (!select) return;
        [...new Set(values.filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, this.dataset.locale || 'de'))
          .forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            select.appendChild(option);
          });
      });
    }

    bindEvents() {
      this.querySelectorAll('[data-atlas-mode]').forEach((button) => {
        button.addEventListener('click', () => {
          this.mode = button.dataset.atlasMode === 'shop' ? 'shop' : 'all';
          this.syncModeButtons();
          this.applyFilters();
        });
      });

      this.search?.addEventListener('input', () => this.applyFilters());

      this.querySelectorAll('[data-atlas-filter]').forEach((select) => {
        select.addEventListener('change', () => this.applyFilters());
      });

      this.availableOnly?.addEventListener('change', () => this.applyFilters());
      this.cityToggle?.addEventListener('change', () => {
        this.showCities = Boolean(this.cityToggle.checked);
        this.cityToggle.setAttribute('aria-checked', String(this.showCities));
        try {
          window.sessionStorage.setItem('nippon-vino-weinatlas-show-cities', String(this.showCities));
        } catch (error) {
          // The control remains functional if storage is unavailable.
        }
        this.renderer?.setCitiesVisible?.(this.showCities);
      });
      this.querySelector('[data-atlas-reset]')?.addEventListener('click', () => this.resetFilters());
      this.querySelector('[data-atlas-share]')?.addEventListener('click', (event) => this.copyView(event.currentTarget));
      this.querySelector('[data-atlas-close-detail]')?.addEventListener('click', () => this.closeDetail());
      this.filterToggle?.addEventListener('click', () => this.toggleFilters());
      this.filterClose?.addEventListener('click', () => this.closeFilters());
      this.filterBackdrop?.addEventListener('click', () => this.closeFilters());

      this.querySelectorAll('[data-atlas-open-region]').forEach((card) => {
        card.addEventListener('click', (event) => {
          if (event.target.closest('a, details')) return;
          this.selectRegion(card.dataset.atlasOpenRegion, { focusDetail: true });
          this.scrollIntoWorkspace();
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.filterPanel?.classList.contains('is-open')) {
          this.closeFilters();
        } else if (event.key === 'Escape' && this.detail?.classList.contains('is-expanded') && this.mobileExperience?.isActive()) {
          const expandButton = this.detail.querySelector('[data-atlas-expand-detail]');
          if (expandButton) this.toggleDetailExpansion(expandButton);
        } else if (event.key === 'Escape' && this.selectedId) {
          this.closeDetail();
        }
      });

      if (this.useFixedLevels) {
        this.querySelectorAll('[data-atlas-level]').forEach((button) => {
          button.addEventListener('click', () => this.requestMapLevel(button.dataset.atlasLevel));
        });
      } else {
        this.querySelector('[data-atlas-zoom-in]')?.addEventListener('click', () => this.zoomBy(0.2));
        this.querySelector('[data-atlas-zoom-out]')?.addEventListener('click', () => this.zoomBy(-0.2));
        this.querySelector('[data-atlas-zoom-reset]')?.addEventListener('click', () => this.resetView());

        this.mapCanvas?.addEventListener('wheel', (event) => {
          event.preventDefault();
          this.zoomBy(event.deltaY > 0 ? -0.12 : 0.12);
        }, { passive: false });

      }

      this.detailContent?.addEventListener('click', (event) => {
        const expandButton = event.target.closest('[data-atlas-expand-detail]');
        if (expandButton) {
          this.toggleDetailExpansion(expandButton);
          return;
        }
        const overviewButton = event.target.closest('[data-atlas-show-in-directory]');
        if (overviewButton) {
          this.showInDirectory(overviewButton.dataset.atlasShowInDirectory);
          return;
        }
        const tab = event.target.closest('[data-detail-tab]');
        if (tab) {
          this.activateDetailTab(tab.dataset.detailTab);
          return;
        }
        const compareButton = event.target.closest('[data-compare-region]');
        if (compareButton) this.toggleCompare(compareButton.dataset.compareRegion);
      });

      // Keep the accessible keyboard navigation from the last functional map
      // version while retaining the localized labels above.
      this.detailContent?.addEventListener('keydown', (event) => {
        const currentTab = event.target.closest('[data-detail-tab]');
        if (!currentTab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const tabs = Array.from(this.detailContent.querySelectorAll('[data-detail-tab]'));
        const currentIndex = tabs.indexOf(currentTab);
        if (currentIndex < 0) return;

        event.preventDefault();
        let nextIndex = currentIndex;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        const nextTab = tabs[nextIndex];
        this.activateDetailTab(nextTab.dataset.detailTab);
        nextTab.focus();
      });

      this.compare?.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-compare-remove]');
        if (remove) {
          this.toggleCompare(remove.dataset.compareRemove);
          return;
        }
        if (event.target.closest('[data-compare-clear]')) {
          this.compareIds = [];
          this.renderCompare();
          this.renderDetail(this.regionById.get(this.selectedId));
        }
      });
    }

    async loadMap() {
      if (!this.mapElement) return;
      try {
        const geojson = window.NipponVinoWineAtlasPrefectures;
        if (!Array.isArray(geojson?.features) || geojson.features.length !== 47) {
          throw new Error('Ungültige Präfekturdaten');
        }
        const Renderer = window.NipponVinoWineAtlasRenderers?.[this.mapModel];
        if (typeof Renderer !== 'function') throw new Error(`Kartenrenderer ${this.mapModel} ist nicht verfügbar`);
        this.geoFeatures = geojson.features;
        this.renderer?.destroy?.();
        this.renderer = new Renderer();
        await this.renderer.mount(this.mapElement, {
          controller: this,
          features: this.geoFeatures,
          regions: this.regions,
          regionByCode: this.regionByCode,
          mapCanvas: this.mapCanvas,
          mapStage: this.mapStage,
          mapUrl: this.dataset.mapUrl,
        }, {
          selectedId: this.selectedId,
          matchingIds: this.matchingIds || new Set(),
          level: this.mapLevel,
          mode: this.mode,
          showCities: this.showCities,
        });
        this.svg = this.renderer.svg;
        this.setupMapResize();

        if (this.initialRegionId) {
          if (this.regionById.has(this.initialRegionId)) {
            this.selectRegion(this.initialRegionId, {
              updateUrl: false,
              focusDetail: false,
              mapLevel: this.initialLevel,
              animateMap: false,
            });
          } else if (/^prefecture-\d{2}$/.test(this.initialRegionId)) {
            const code = this.initialRegionId.slice(-2);
            const feature = this.geoFeatures.find((item) => String(item.properties?.code).padStart(2, '0') === code);
            if (feature) {
              const name = window.NipponVinoWineAtlasRenderers?.prefectureNames?.[Number(code) - 1] || `Präfektur ${Number(code)}`;
              this.selectPrefecture(code, name, feature.properties?.nameJa || '', {
                updateUrl: false,
                focusDetail: false,
                mapLevel: this.initialLevel,
                animateMap: false,
              });
            }
          }
        } else {
          this.setMapLevel('japan', { animate: false, updateUrl: false });
        }
        this.initialRegionId = '';
        this.initialLevel = '';
      } catch (error) {
        this.showMapError(error);
      }
    }

    setupMapResize() {
      this.mapResizeObserver?.disconnect();
      if (!('ResizeObserver' in window) || !this.mapCanvas) return;
      let lastWidth = Math.round(this.mapCanvas.clientWidth || 0);
      this.mapResizeObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0]?.contentRect?.width || 0);
        if (!width || Math.abs(width - lastWidth) < 2) return;
        lastWidth = width;
        if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          this.renderer?.resize?.();
          this.svg = this.renderer?.svg;
        });
      });
      this.mapResizeObserver.observe(this.mapCanvas);
    }

    showMapError(error) {
      this.mapElement?.setAttribute('aria-busy', 'false');
      if (this.mapElement) this.mapElement.dataset.error = String(error?.message || error || 'Unbekannter Kartenfehler');
      if (this.mapElement) {
        this.mapElement.innerHTML = `
          <div class="wine-atlas__map-error" role="alert">
            <img class="wine-atlas__map-fallback" src="${this.escapeAttribute(this.dataset.fallbackMapUrl || '')}" alt="${this.escapeAttribute(this.t('interactive_map'))}" width="1000" height="1000">
            <p><strong>${this.escape(this.t('map_error_title'))}</strong><br>${this.escape(this.t('map_error_text'))}</p>
          </div>`;
      }
      this.setStatus(this.t('map_error_status'));
      if (window.Shopify?.designMode) console.error('[Nippon Vino Weinatlas]', error);
    }

    selectPrefecture(code, name, japaneseName, options = {}) {
      const id = `prefecture-${String(code).padStart(2, '0')}`;
      if (!this.regionById.has(id)) {
        const fallback = this.normalizeRegion({
          id,
          prefCode: code,
          name,
          japaneseName,
          prefecture: name,
          area: name,
          summary: this.t('fallback_summary', { region: name }),
          climate: '',
          soil: '',
          grapes: [],
          styles: [],
          wineryCount: null,
          availableWineries: 0,
          availableWines: 0,
          sourceLabel: this.t('not_curated'),
          isFallback: true,
        });
        fallback.isFallback = true;
        this.fallbackRegions.set(id, fallback);
        this.regionById.set(id, fallback);
      }
      this.selectRegion(id, options);
    }

    showTooltip(region) {
      if (!this.tooltip) return;
      const shopText = region.isFallback
        ? this.t('short_profile')
        : region.availableWines > 0
          ? this.t('shop_wine_count', { count: region.availableWines, wines: this.wineCount(region.availableWines) })
          : this.t('winery_count_in_prefecture', { count: region.wineryCount ?? '–' });
      this.tooltip.innerHTML = `<strong>${this.escape(region.name)}</strong><span>${this.escape(shopText)}</span>`;
      this.tooltip.hidden = false;
    }

    hideTooltip() {
      if (this.tooltip) this.tooltip.hidden = true;
    }

    syncModeButtons() {
      this.querySelectorAll('[data-atlas-mode]').forEach((button) => {
        const active = button.dataset.atlasMode === this.mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    syncLevelButtons() {
      if (!this.useFixedLevels) return;
      this.querySelectorAll('[data-atlas-level]').forEach((button) => {
        const level = button.dataset.atlasLevel;
        const active = level === this.mapLevel;
        const requiresRegion = level === 'region' || level === 'detail';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        const keepCurrentPreset = ['isometric-a-2-1', 'isometric-a-2'].includes(this.mapModel) && active;
        button.disabled = requiresRegion && !this.selectedId && !keepCurrentPreset;
      });

    }

    requestMapLevel(level) {
      if (!this.useFixedLevels) return;
      if (level === 'japan') {
        if (this.selectedId) {
          this.closeDetail({ restoreFocus: false, resetLevel: true });
        } else {
          this.setMapLevel('japan');
          this.setStatus(this.t('map_ready'));
        }
        return;
      }
      if (!this.selectedId) return;
      this.setMapLevel(level);
      const region = this.regionById.get(this.selectedId);
      this.setStatus(this.t('map_level_status', {
        region: region?.name || this.t('region'),
        level: level === 'detail' ? 'Detail' : this.t('region'),
      }));
    }

    setMapLevel(level, options = {}) {
      const valid = ['japan', 'region', 'detail'].includes(level) ? level : 'japan';
      const mayKeepPreset = ['isometric-a-2-1', 'isometric-a-2'].includes(this.mapModel) && valid === this.mapLevel;
      this.mapLevel = valid !== 'japan' && !this.selectedId && !mayKeepPreset ? 'japan' : valid;
      this.dataset.mapLevel = this.mapLevel;
      this.syncLevelButtons();
      this.renderer?.setLevel?.(this.mapLevel, options);
      this.mobileExperience?.update();
      if (options.updateUrl !== false) this.updateUrl();
    }

    zoomBy(delta) {
      this.renderer?.zoomBy?.(delta);
    }

    resetView() {
      this.renderer?.resetView?.();
    }

    applyFilters(updateUrl = true) {
      this.filters = {};
      this.querySelectorAll('[data-atlas-filter]').forEach((select) => {
        if (select.value) this.filters[select.dataset.atlasFilter] = select.value;
      });

      const matching = this.regions.filter((region) => this.regionMatches(region));
      const matchingIds = new Set(matching.map((region) => region.id));
      this.matchingIds = matchingIds;

      if (this.selectedId && !matchingIds.has(this.selectedId)) {
        this.closeDetail({
          restoreFocus: false,
          updateMap: false,
          updateUrl: false,
          setStatus: false,
        });
      }

      this.applyDirectoryState(matchingIds);
      if (this.resultCount) this.resultCount.textContent = String(matching.length);
      this.updateFilterCount();
      this.applyMapState(matchingIds);
      this.mobileExperience?.update();
      this.setStatus(
        matching.length
          ? this.t('regions_count', { count: matching.length })
          : this.t('no_results')
      );
      if (updateUrl) this.updateUrl();
    }

    regionMatches(region) {
      if (this.mode === 'shop' && region.availableWines < 1) return false;
      if (this.availableOnly?.checked && region.availableWines < 1) return false;

      const query = this.normalizeText(this.search?.value || '');
      if (query) {
        const haystack = this.normalizeText([
          region.name,
          region.japaneseName,
          region.prefecture,
          region.area,
          region.summary,
          region.climate,
          region.soil,
          ...region.grapes,
          ...region.styles,
        ].join(' '));
        if (!haystack.includes(query)) return false;
      }

      return Object.entries(this.filters).every(([key, value]) => {
        if (key === 'start') return this.startBucket(region.wineStart) === value;
        if (key === 'wineries') return this.wineryBucket(region.wineryCount) === value;
        if (key === 'grapes' || key === 'styles') return region[key].includes(value);
        if (key === 'harvest_band') return region.harvestBand === value;
        if (key === 'elevation_band') return region.elevationBand === value;
        return String(region[key] || '') === value;
      });
    }

    startBucket(value) {
      const year = Number(String(value || '').match(/\d{4}/)?.[0]);
      if (!year) return 'unknown';
      if (year < 1900) return 'before-1900';
      if (year < 2000) return '1900-1999';
      return 'since-2000';
    }

    wineryBucket(value) {
      if (value === null) return 'unknown';
      if (value >= 50) return '50-plus';
      if (value >= 10) return '10-49';
      return 'under-10';
    }

    applyMapState(matchingIds = null) {
      const visibleIds = matchingIds || new Set(
        this.regions.filter((region) => this.regionMatches(region)).map((region) => region.id)
      );
      this.renderer?.update?.(visibleIds, {
        selectedId: this.selectedId,
        level: this.mapLevel,
        mode: this.mode,
        showCities: this.showCities,
      });
    }

    resetFilters() {
      this.search.value = '';
      this.querySelectorAll('[data-atlas-filter]').forEach((select) => {
        select.value = '';
      });
      if (this.availableOnly) this.availableOnly.checked = false;
      this.applyFilters();
    }

    updateFilterCount() {
      const selectedFilters = [...this.querySelectorAll('[data-atlas-filter]')]
        .filter((select) => Boolean(select.value)).length;
      const count = selectedFilters + (this.availableOnly?.checked ? 1 : 0);

      if (this.filterCount) {
        this.filterCount.textContent = `· ${count}`;
        this.filterCount.hidden = count === 0;
      }
      if (this.filterToggle) {
        this.filterToggle.classList.toggle('has-active-filters', count > 0);
        this.filterToggle.setAttribute(
          'aria-label',
          count > 0 ? this.t('filter_open_count', { count }) : this.t('filter_open')
        );
      }
    }

    toggleFilters() {
      if (this.filterPanel?.classList.contains('is-open')) this.closeFilters();
      else this.openFilters();
    }

    openFilters() {
      this.filterPanel?.classList.add('is-open');
      this.filterBackdrop?.classList.add('is-open');
      this.filterPanel?.setAttribute('aria-hidden', 'false');
      this.filterBackdrop?.setAttribute('aria-hidden', 'false');
      this.filterToggle?.setAttribute('aria-expanded', 'true');
      this.filterClose?.focus({ preventScroll: true });
    }

    closeFilters(options = {}) {
      this.filterPanel?.classList.remove('is-open');
      this.filterBackdrop?.classList.remove('is-open');
      this.filterPanel?.setAttribute('aria-hidden', 'true');
      this.filterBackdrop?.setAttribute('aria-hidden', 'true');
      this.filterToggle?.setAttribute('aria-expanded', 'false');
      if (options.restoreFocus !== false) this.filterToggle?.focus({ preventScroll: true });
    }

    selectRegion(id, options = {}) {
      const region = this.regionById.get(id);
      if (!region) return;
      if (this.mobileExperience?.isActive()) {
        this.mobileExperience.rememberTrigger(options.sourceTrigger || document.activeElement);
      }
      if (!this.renderDetail(region)) return;
      this.selectedId = id;
      if (this.detail) {
        this.detail.hidden = false;
        this.detail.scrollTop = 0;
      }
      this.detail?.classList.add('is-open');
      this.detail?.classList.remove('is-expanded');
      this.detail?.classList.toggle('is-shop-region', region.availableWines > 0);
      this.workspace?.classList.add('has-detail');
      this.renderer?.selectRegion?.(id);
      this.applyMapState();
      this.applyDirectoryState();
      this.mobileExperience?.update();
      if (this.useFixedLevels) {
        const requestedLevel = options.mapLevel === 'detail' || (this.mapLevel === 'detail' && options.mapLevel !== 'region')
          ? 'detail'
          : 'region';
        this.setMapLevel(requestedLevel, {
          animate: options.animateMap !== false,
          updateUrl: false,
        });
      }
      this.setStatus(`${this.t('region_selected', { region: region.name })} ${region.summary || ''}`.trim());
      if (options.updateUrl !== false) this.updateUrl();
      if (options.focusDetail) {
        const heading = this.detail?.querySelector('h2');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
      }
    }

    renderDetail(region) {
      if (!region || !this.detailContent) return false;
      const inCompare = this.compareIds.includes(region.id);
      const compareDisabled = !inCompare && this.compareIds.length >= 3;
      const timeline = this.parseTimeline(region.timeline);
      const source = region.sourceUrl
        ? `<a href="${this.escapeAttribute(this.safeUrl(region.sourceUrl))}" target="_blank" rel="noopener">${this.escape(region.sourceLabel || this.t('source_open'))}</a>`
        : this.escape(region.sourceLabel || this.t('source_missing'));
      const location = region.area && region.area !== region.prefecture
        ? `${region.prefecture} · ${region.area}`
        : region.prefecture || region.area;
      const hasTerroir = [region.climate, region.soil, region.elevation, region.area]
        .some((value) => this.isVerifiedContent(value));
      const hasHarvest = Boolean(region.harvest);
      const hasTimeline = timeline.length > 0;
      const hasTimeContent = hasTimeline || hasHarvest;

      this.detailContent.innerHTML = `
        <header class="wine-atlas__detail-head">
          <div class="wine-atlas__detail-badges">
            <span class="wine-atlas__badge">${this.escape(region.gi || this.t('regions_profile'))}</span>
            ${region.availableWines > 0
              ? `<span class="wine-atlas__badge">${this.escape(this.t('available_in_shop'))}</span>`
              : `<span class="wine-atlas__badge wine-atlas__badge--muted">${this.escape(this.t('current_unavailable'))}</span>`}
          </div>
          <span class="wine-atlas__detail-japanese" lang="ja">${this.escape(region.japaneseName)}</span>
          <h2 id="WineAtlasDetailTitle-${this.escapeAttribute(region.id)}">${this.escape(region.name)}</h2>
          <p class="wine-atlas__detail-location">${this.escape(location)}</p>
          ${region.atlas?.capital?.name ? `<p class="wine-atlas__detail-capital">${this.escape(this.t('capital'))}: <strong>${this.escape(region.atlas.capital.name)}</strong></p>` : ''}
        </header>
        <div class="wine-atlas__facts">
          ${this.renderFact(this.t('winegrowing_since'), this.formatWineStart(region.wineStart), 'vine')}
          ${this.renderFact(this.t('wineries'), region.wineryCount ?? '–', 'winery')}
          ${this.renderFact(this.t('in_shop'), this.wineCount(region.availableWines), 'shop', region.availableWines > 0)}
        </div>
        ${this.renderTravel(region)}
        <button class="wine-atlas__detail-expand" type="button" data-atlas-expand-detail aria-expanded="false">
          <span>${this.mobileExperience?.isActive() ? this.t('expand_profile') : this.t('show_more')}</span>
          <i aria-hidden="true">↑</i>
        </button>
        <div class="wine-atlas__detail-tabs" role="tablist" aria-label="${this.escapeAttribute(region.name)}">
          ${this.renderTab('overview', this.t('overview'), true)}
          ${hasTerroir ? this.renderTab('terroir', this.t('terroir'), false) : ''}
          ${hasTimeContent ? this.renderTab('time', this.t('growing_harvest'), false) : ''}
          ${this.renderTab('shop', this.t('our_wines'), false)}
          ${this.renderTab('wineries', this.t('wineries'), false)}
        </div>
        <div class="wine-atlas__detail-body">
          <section class="wine-atlas__detail-panel" data-detail-panel="overview" role="tabpanel">
            ${region.summary ? `<p class="wine-atlas__summary">${this.escape(region.summary)}</p>` : ''}
            ${region.regionPage ? `
              <p class="wine-atlas__region-page-link">
                <a href="${this.escapeAttribute(this.safeUrl(region.regionPage))}">${this.escape(this.t('region_page'))}</a>
              </p>
            ` : ''}
            ${this.renderChipSection(this.t('grapes'), region.grapes)}
            ${this.renderChipSection(this.t('typical_styles'), region.styles)}
            ${region.isFallback ? '' : `
              <div class="wine-atlas__detail-actions">
                <button
                  class="wine-atlas__compare-button"
                  type="button"
                  data-compare-region="${this.escapeAttribute(region.id)}"
                  ${compareDisabled ? 'disabled' : ''}
                >
                  ${inCompare ? this.t('remove_compare') : compareDisabled ? this.t('max_compare') : this.t('add_compare')}
                </button>
                <button
                  class="wine-atlas__directory-button"
                  type="button"
                  data-atlas-show-in-directory="${this.escapeAttribute(region.id)}"
                >
                  ${this.t('show_directory')}
                </button>
              </div>
            `}
          </section>
          ${hasTerroir ? `<section class="wine-atlas__detail-panel" data-detail-panel="terroir" role="tabpanel" hidden>
            <dl class="wine-atlas__meta-list">
              ${this.renderDefinition(this.t('climate'), region.climate)}
              ${this.renderDefinition(this.t('soil'), region.soil)}
              ${this.renderDefinition(this.t('elevation'), region.elevation)}
              ${this.renderDefinition(this.t('location'), region.area)}
            </dl>
          </section>` : ''}
          ${hasTimeContent ? `<section class="wine-atlas__detail-panel" data-detail-panel="time" role="tabpanel" hidden>
            ${hasTimeline ? `<div class="wine-atlas__detail-section">
              <h3>${this.escape(this.t('development'))}</h3>
              ${this.renderTimeline(timeline)}
            </div>` : ''}
            ${hasHarvest ? `<div class="wine-atlas__detail-section">
              <h3>${this.escape(this.t('typical_harvest'))}</h3>
              <p class="wine-atlas__summary">${this.escape(region.harvest)}</p>
              ${this.renderCalendar(region.harvestBand)}
            </div>` : ''}
          </section>` : ''}
          <section class="wine-atlas__detail-panel" data-detail-panel="shop" role="tabpanel" hidden>
            ${this.renderShop(region)}
          </section>
          <section class="wine-atlas__detail-panel" data-detail-panel="wineries" role="tabpanel" hidden>
            ${this.renderWineries(region)}
          </section>
          <p class="wine-atlas__source">
            ${this.escape(this.t('source'))}: ${source}<br>
            ${this.escape(this.t('data_status'))}: ${this.escape(region.updatedAt || this.t('not_specified'))}.
            ${this.escape(this.t('source_note'))}
          </p>
        </div>
      `;
      this.detail?.setAttribute('aria-labelledby', `WineAtlasDetailTitle-${region.id}`);
      return true;
    }

    renderTravel(region) {
      const atlas = region.atlas || {};
      const referenceCity = atlas.referenceCity;
      const wineHub = atlas.wineHub;
      const primary = atlas.travel;
      if (!referenceCity || !wineHub || !primary) return '';

      let distance = this.haversineDistance(referenceCity, wineHub);
      if (distance < 1 && primary.fromCoordinates && primary.toCoordinates) {
        distance = this.haversineDistance(primary.fromCoordinates, primary.toCoordinates);
      }
      const proximity = this.localizeTravelText(wineHub.proximityText || `${wineHub.name} · von ${referenceCity.name}`);
      const additional = [atlas.secondaryTravel, ...(atlas.additionalTravel || [])].filter(Boolean);

      return `
        <section class="wine-atlas__travel" aria-label="${this.escapeAttribute(this.t('location_access'))}">
          <span class="wine-atlas__travel-kicker">${this.escape(this.t('location_access'))}</span>
          <div class="wine-atlas__travel-proximity">
            <strong>${this.escape(proximity)}</strong>
            <span>${this.escape(this.t('distance_value', { distance: Math.round(distance) }))}</span>
          </div>
          ${this.renderTravelRoute(primary, true)}
          ${additional.length ? `
            <details class="wine-atlas__travel-more">
              <summary>${this.escape(this.t(additional.length === 1 ? 'one_connection' : 'other_connections', { count: additional.length }))}</summary>
              <div>${additional.map((travel) => this.renderTravelRoute(travel, false)).join('')}</div>
            </details>
          ` : ''}
        </section>
      `;
    }

    renderTravelRoute(travel, primary) {
      const sourceUrl = this.safeUrl(travel.sourceUrl || '#');
      const secondarySource = travel.secondarySourceUrl
        ? ` · <a href="${this.escapeAttribute(this.safeUrl(travel.secondarySourceUrl))}" target="_blank" rel="noopener">${this.escape(this.t('timetable'))}</a>`
        : '';
      return `
        <div class="wine-atlas__travel-route${primary ? ' wine-atlas__travel-route--primary' : ''}">
          <span class="wine-atlas__travel-from">${this.escape(travel.from)} → ${this.escape(travel.to)}</span>
          <strong>${this.escape(this.localizeTravelText(travel.duration))}</strong>
          <span>${this.escape(this.localizeTravelText(travel.mode))}</span>
          ${travel.note ? `<small>${this.escape(this.localizeTravelText(travel.note))}</small>` : ''}
          <small>
            ${this.escape(this.t('last_updated'))} ${this.escape(travel.verifiedAt || '11.08.2026')} ·
            <a href="${this.escapeAttribute(sourceUrl)}" target="_blank" rel="noopener">${this.escape(travel.sourceLabel || this.t('source'))}</a>${secondarySource}
          </small>
        </div>
      `;
    }

    haversineDistance(from, to) {
      const toRadians = (value) => Number(value) * Math.PI / 180;
      const latitude1 = toRadians(from.latitude);
      const latitude2 = toRadians(to.latitude);
      const deltaLatitude = latitude2 - latitude1;
      const deltaLongitude = toRadians(to.longitude) - toRadians(from.longitude);
      const value = Math.sin(deltaLatitude / 2) ** 2
        + Math.cos(latitude1) * Math.cos(latitude2) * (Math.sin(deltaLongitude / 2) ** 2);
      return 6371.0088 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
    }

    localizeTravelText(value) {
      const text = String(value || '');
      if (this.dataset.locale !== 'en') return text;
      return text
        .replace('westlich von Tokio', 'west of Tokyo')
        .replace('östlich von Tokio', 'east of Tokyo')
        .replace('südlich von Tokio', 'south of Tokyo')
        .replace('nördlich von Tokio', 'north of Tokyo')
        .replace('nordwestlich von Tokio', 'northwest of Tokyo')
        .replace('nordöstlich von Tokio', 'northeast of Tokyo')
        .replace('südöstlich von Osaka', 'southeast of Osaka')
        .replace('westlich von Sapporo', 'west of Sapporo')
        .replace('westlich von Matsue', 'west of Matsue')
        .replace('an der Küste des Japanischen Meeres', 'on the Sea of Japan coast')
        .replace('Regionsstadt und Weinanker', 'regional city and wine hub')
        .replace('Tor zum Weinland Iwate', 'gateway to Iwate wine country')
        .replace(/ca\.\s*/g, 'approx. ')
        .replace(/Std\./g, 'hr')
        .replace(/Min\./g, 'min')
        .replace('Flugzeit', 'Flight time')
        .replace('insgesamt', 'total');
    }

    factIcon(type) {
      const paths = {
        vine: '<path d="M12 4c1.8 1.1 2.7 2.5 2.7 4.2-1.8.1-3.3-.7-4.4-2.2M12.2 7.2c-.8 2.2-2.2 3.7-4.1 4.7"/><circle cx="8.4" cy="13.2" r="2"/><circle cx="12.1" cy="12.5" r="2"/><circle cx="10.2" cy="16.2" r="2"/><circle cx="13.8" cy="15.7" r="2"/><circle cx="12" cy="19" r="1.7"/>',
        winery: '<path d="M3.5 11.2 12 5l8.5 6.2"/><path d="M5.3 10.2V20h13.4v-9.8M9.2 20v-5.7h5.6V20M7.5 9.2V6.4h2.3v1.1"/><path d="M16.2 12.7h.1"/>',
        shop: '<path d="M8.7 8.3V5.8c0-.9.7-1.6 1.6-1.6h3.4c.9 0 1.6.7 1.6 1.6v2.5"/><path d="M5.2 8.3h13.6l-1 11.5H6.2L5.2 8.3Z"/><path d="M10.2 12.1v4.2c0 .8.8 1.5 1.8 1.5s1.8-.7 1.8-1.5v-4.2M10.2 13.2h3.6"/>',
      };
      return `<svg class="wine-atlas__fact-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${paths[type] || paths.vine}</svg>`;
    }

    renderFact(label, value, icon, shop = false) {
      return `
        <div class="wine-atlas__fact${shop ? ' wine-atlas__fact--shop' : ''}">
          <div class="wine-atlas__fact-heading">
            ${this.factIcon(icon)}
            <span>${this.escape(String(label))}</span>
          </div>
          <strong>${this.escape(String(value))}</strong>
        </div>
      `;
    }

    renderTab(id, label, active) {
      return `
        <button
          class="wine-atlas__detail-tab"
          type="button"
          role="tab"
          data-detail-tab="${id}"
          aria-selected="${active}"
        >${this.escape(label)}</button>
      `;
    }

    activateDetailTab(id) {
      this.detailContent.querySelectorAll('[data-detail-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.detailTab === id));
      });
      this.detailContent.querySelectorAll('[data-detail-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.detailPanel !== id;
      });
    }

    renderChipSection(title, values) {
      const chips = (values || []).map((value) => `<span class="wine-atlas__chip">${this.escape(value)}</span>`).join('');
      if (!chips) return '';
      return `
        <div class="wine-atlas__detail-section">
          <h3>${this.escape(title)}</h3>
          <div class="wine-atlas__chips">${chips}</div>
        </div>
      `;
    }

    renderDefinition(label, value) {
      if (!this.isVerifiedContent(value)) return '';
      return `
        <div>
          <dt>${this.escape(label)}</dt>
          <dd>${this.escape(value)}</dd>
        </div>
      `;
    }

    parseTimeline(value) {
      return String(value || '')
        .split(/\r?\n/)
        .map((line) => {
          const [year, ...description] = line.split('|');
          return { year: year?.trim(), description: description.join('|').trim() };
        })
        .filter((entry) => entry.year && entry.description);
    }

    renderTimeline(entries) {
      if (!entries.length) return '';
      return `
        <ol class="wine-atlas__timeline">
          ${entries.map((entry) => `
            <li><strong>${this.escape(entry.year)}</strong><span>${this.escape(entry.description)}</span></li>
          `).join('')}
        </ol>
      `;
    }

    renderCalendar(band) {
      const harvestMonths = {
        'aug-oct': [7, 8, 9],
        'sep-oct': [8, 9],
        variable: [7, 8, 9, 10],
        unknown: [],
      }[band] || [];
      const months = Array.isArray(this.copy.months)
        ? this.copy.months
        : String(this.copy.months || 'Jan|Feb|Mär|Apr|Mai|Jun|Jul|Aug|Sep|Okt|Nov|Dez').split('|');
      return `
        <div class="wine-atlas__calendar" aria-label="${this.escapeAttribute(this.t('harvest_calendar'))}">
          ${months.map((month, index) => `
            <span class="wine-atlas__month ${harvestMonths.includes(index) ? 'is-harvest' : ''}">
              <i aria-hidden="true"></i><span>${month}</span>
            </span>
          `).join('')}
        </div>
      `;
    }

    renderShop(region) {
      if (region.availableWines < 1) {
        return `
          <div class="wine-atlas__shop-callout">
            <strong>${this.escape(this.t('current_unavailable'))}</strong>
            <span>${this.escape(this.t('no_wines_in_region', { region: region.name }))}</span>
          </div>
        `;
      }

      const visibleProducts = region.originProducts || [];
      const products = visibleProducts.length
        ? `<div class="wine-atlas__products">${visibleProducts.map((product) => this.renderProduct(product)).join('')}</div>`
        : `<p class="wine-atlas__summary">${this.escape(this.t('no_wines_in_region', { region: region.name }))}</p>`;

      return `
        <div class="wine-atlas__shop-callout">
          <strong>${this.escape(region.name)}</strong>
          <span>${this.escape(this.t('products_from_region', { region: region.name }))}: ${this.escape(this.wineCount(region.availableWines))}</span>
        </div>
        ${products}
      `;
    }

    renderProduct(product) {
      const image = product.image
        ? `<img src="${this.escapeAttribute(this.safeUrl(product.image))}" alt="${this.escapeAttribute(product.title || 'Japanischer Wein')}" loading="lazy" width="90" height="110">`
        : '<span class="wine-atlas__product-placeholder" aria-hidden="true">葡</span>';
      const crossRegionDetails = product.wineryRegion && product.primaryRegion && product.wineryRegion !== product.primaryRegion
        ? `<small class="wine-atlas__product-relation"><b>${this.escape(this.t('grape_origin'))}:</b> ${this.escape(product.grapeOriginLocation || product.grapeOriginRegion)}<br><b>${this.escape(this.t('winery'))}:</b> ${this.escape(product.wineryName || '')}</small>`
        : '';
      return `
        <a class="wine-atlas__product" href="${this.escapeAttribute(this.safeUrl(product.url || '#'))}">
          ${image}
          <span><strong>${this.escape(product.title || 'Japanese wine')}</strong><span>${this.escape(product.price || '')}</span>${crossRegionDetails}</span>
          <em aria-hidden="true">→</em>
        </a>
      `;
    }

    renderWineries(region) {
      const local = region.localWineries || [];
      const wineRelationships = (region.originProducts || []).filter((product) => product.wineryRegion && product.wineryRegion !== region.id);
      const renderLocal = local.length
        ? `<div class="wine-atlas__winery-grid">${local.map((winery) => this.renderWineryCard(winery)).join('')}</div>`
        : `<div class="wine-atlas__winery-empty"><p>${this.escape(this.t('no_wineries', { region: region.name }))}</p><p>${this.escape(this.t('overview_grows'))}</p></div>`;
      const remote = wineRelationships.length
        ? `<section class="wine-atlas__winery-group wine-atlas__winery-group--cross-region">
            <h3>${this.escape(this.t('wineries_with_wines'))}</h3>
            <div class="wine-atlas__winery-grid">${wineRelationships.map((product) => this.renderCrossRegionWinery(product, region)).join('')}</div>
          </section>`
        : '';
      return `
        <section class="wine-atlas__winery-group">
          <h3>${this.escape(this.t('wineries_in_region'))}</h3>
          ${renderLocal}
        </section>
        ${remote}
      `;
    }

    renderWineryCard(winery) {
      const isMarufuji = /marufuji/i.test(String(winery.name || ''));
      const isChanmoris = /chanmoris/i.test(String(winery.name || '')) || winery.key === 'chanmoris';
      const url = this.safeUrl(winery.url || (isMarufuji ? '/pages/rubaiyat' : '#'));
      const linkLabel = winery.key === 'chanmoris' ? this.t('discover_chanmoris') : this.t('discover_winery');
      const location = [winery.city, winery.founded ? this.t('winery_since', { year: winery.founded }) : ''].filter(Boolean).join(' · ');
      const description = this.dataset.locale === 'en'
        ? (isMarufuji ? this.t('marufuji_description') : isChanmoris ? this.t('chanmoris_description') : winery.description)
        : winery.description;
      const image = winery.image
        ? `<div class="wine-atlas__winery-media"><img src="${this.escapeAttribute(this.safeUrl(winery.image))}" alt="${this.escapeAttribute(winery.imageAlt || winery.name || '')}" loading="lazy" width="720" height="405"></div>`
        : '';
      const link = url !== '#'
        ? `<a class="wine-atlas__winery-link" href="${this.escapeAttribute(url)}">${this.escape(linkLabel)} <span aria-hidden="true">→</span></a>`
        : '';
      return `<article class="wine-atlas__winery-card">
        ${image}
        <div class="wine-atlas__winery-content">
          ${location ? `<p class="wine-atlas__winery-location">${this.escape(location)}</p>` : ''}
          <h3>${this.escape(winery.name || this.t('winery'))}</h3>
          ${description ? `<p>${this.escape(description)}</p>` : ''}
          ${link}
        </div>
      </article>`;
    }

    renderCrossRegionWinery(product, region) {
      const wineryRegion = this.regions.find((item) => item.id === product.wineryRegion)?.name || product.wineryRegion;
      const wineryUrl = this.safeUrl(product.wineryUrl || '#');
      return `<article class="wine-atlas__winery-card wine-atlas__winery-card--cross-region">
        <span class="wine-atlas__winery-badge">${this.escape(this.t('winery_in_region_wine_from', { winery_region: String(wineryRegion).toUpperCase(), wine_region: String(region.name).toUpperCase() }))}</span>
        <h4>${this.escape(product.wineryName)}</h4>
        <p>${this.escape(this.t('merlot_cross_region'))}</p>
        <p class="wine-atlas__winery-actions">
          <a class="wine-atlas__winery-link" href="${this.escapeAttribute(wineryUrl)}">${this.escape(this.t('discover_winery'))}</a>
          <a class="wine-atlas__winery-link" href="${this.escapeAttribute(this.safeUrl(product.url || '#'))}">${this.escape(this.t('discover_merlot'))}</a>
        </p>
      </article>`;
    }

    toggleCompare(id) {
      if (!this.regionById.has(id)) return;
      if (this.compareIds.includes(id)) {
        this.compareIds = this.compareIds.filter((compareId) => compareId !== id);
      } else if (this.compareIds.length < 3) {
        this.compareIds.push(id);
      } else {
        this.setStatus(this.t('max_compare_status'));
        return;
      }
      this.renderCompare();
      this.renderDetail(this.regionById.get(this.selectedId));
    }

    renderCompare() {
      if (!this.compare) return;
      const regions = this.compareIds.map((id) => this.regionById.get(id)).filter(Boolean);
      if (!regions.length) {
        this.compare.hidden = true;
        this.compare.innerHTML = '';
        return;
      }

      this.compare.hidden = false;
      const headerCells = regions.map((region) => `
        <td>
          <strong>${this.escape(region.name)}</strong>
          <button class="wine-atlas__compare-remove" type="button" data-compare-remove="${this.escapeAttribute(region.id)}">${this.escape(this.t('remove'))}</button>
        </td>
      `).join('');

      this.compare.innerHTML = `
        <div class="wine-atlas__compare-header">
          <div>
            <span class="wine-atlas__label">${this.escape(this.t('direct_compare', { count: regions.length }))}</span>
            <h2>${this.escape(regions.length < 2 ? this.t('select_one_more_region') : this.t('regions_in_compare'))}</h2>
          </div>
          <button type="button" data-compare-clear>${this.escape(this.t('clear_compare'))}</button>
        </div>
        ${regions.length < 2 ? '' : `
          <div class="wine-atlas__compare-table-wrap">
            <table>
              <tbody>
                <tr><th scope="row">${this.escape(this.t('region'))}</th>${headerCells}</tr>
                ${this.compareRow(this.t('winegrowing_since'), regions, (region) => this.formatWineStart(region.wineStart))}
                ${this.compareRow(this.t('wineries'), regions, (region) => region.wineryCount ?? '–')}
                ${this.compareRow(this.t('harvest_short'), regions, (region) => this.displayContent(region.harvest))}
                ${this.compareRow(this.t('climate'), regions, (region) => this.displayContent(region.climate))}
                ${this.compareRow(this.t('soil'), regions, (region) => this.displayContent(region.soil))}
                ${this.compareRow(this.t('grapes'), regions, (region) => region.grapes.join(', ') || '–')}
                ${this.compareRow(this.t('in_shop'), regions, (region) => this.wineCount(region.availableWines))}
              </tbody>
            </table>
          </div>
        `}
      `;
    }

    compareRow(label, regions, getter) {
      return `<tr><th scope="row">${this.escape(label)}</th>${regions.map((region) => `<td>${this.escape(String(getter(region)))}</td>`).join('')}</tr>`;
    }

    toggleDetailExpansion(button) {
      if (!this.detail) return;
      const expanded = !this.detail.classList.contains('is-expanded');
      this.detail.classList.toggle('is-expanded', expanded);
      this.detail.setAttribute('role', expanded && this.mobileExperience?.isActive() ? 'dialog' : 'complementary');
      if (expanded && this.mobileExperience?.isActive()) this.detail.setAttribute('aria-modal', 'true');
      else this.detail.removeAttribute('aria-modal');
      if (this.useFixedLevels && this.selectedId) {
        this.setMapLevel(expanded ? 'detail' : 'region');
      }
      button.setAttribute('aria-expanded', String(expanded));
      const label = button.querySelector('span');
      if (label) label.textContent = expanded ? this.t('less') : this.t('show_more');
      const icon = button.querySelector('i');
      if (icon) icon.textContent = expanded ? '↓' : '↑';
      this.mobileExperience?.detailExpanded(expanded, button);
    }

    applyDirectoryState(matchingIds = this.matchingIds) {
      const visibleIds = matchingIds || new Set(this.regions.map((region) => region.id));
      this.querySelectorAll('[data-atlas-region-card]').forEach((card) => {
        const selected = card.dataset.atlasRegionCard === this.selectedId;
        card.hidden = !visibleIds.has(card.dataset.atlasRegionCard) && !selected;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-current', selected ? 'true' : 'false');
      });
    }

    showInDirectory(id) {
      const card = this.querySelector(`[data-atlas-region-card="${id}"]`);
      if (!card) {
        this.setStatus(this.t('region_directory_missing'));
        return;
      }
      card.hidden = false;
      card.classList.add('is-selected');
      this.detail?.classList.remove('is-expanded');
      const expandButton = this.detail?.querySelector('[data-atlas-expand-detail]');
      expandButton?.setAttribute('aria-expanded', 'false');
      const expandLabel = expandButton?.querySelector('span');
      if (expandLabel) expandLabel.textContent = this.t('show_more');
      window.requestAnimationFrame(() => {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.querySelector('.wine-atlas__region-cta')?.focus({ preventScroll: true });
      });
    }

    closeDetail(options = {}) {
      const selectedId = this.selectedId;
      this.detail?.classList.remove('is-open');
      this.detail?.classList.remove('is-expanded');
      this.detail?.classList.remove('is-shop-region');
      this.detail?.removeAttribute('aria-modal');
      this.detail?.setAttribute('role', 'complementary');
      if (this.detail) this.detail.hidden = true;
      this.workspace?.classList.remove('has-detail');
      this.selectedId = '';
      if (this.useFixedLevels) {
        const keepCamera = ['isometric-a-2-1', 'isometric-a-2'].includes(this.mapModel)
          && options.resetLevel !== true
          && !this.mobileExperience?.isActive();
        if (!keepCamera) {
          this.mapLevel = 'japan';
          this.dataset.mapLevel = 'japan';
        }
        this.syncLevelButtons();
        this.renderer?.selectRegion?.('');
        if (!keepCamera) this.renderer?.setLevel?.('japan', { animate: options.animateMap !== false });
      }
      if (options.updateMap !== false) this.applyMapState();
      this.applyDirectoryState();
      this.mobileExperience?.update();
      if (options.updateUrl !== false) this.updateUrl();
      if (options.setStatus !== false) {
        this.setStatus(this.t('map_status'));
      }
      if (options.restoreFocus !== false) {
        const restored = this.mobileExperience?.restoreFocus(selectedId);
        if (!restored) this.querySelector(`[data-atlas-region="${selectedId}"]`)?.focus?.();
      }
    }

    readCityPreference() {
      if (!this.cityToggle) return false;
      try {
        return window.sessionStorage.getItem('nippon-vino-weinatlas-show-cities') === 'true';
      } catch (error) {
        return false;
      }
    }

    syncCityToggle() {
      if (!this.cityToggle) return;
      this.cityToggle.checked = this.showCities;
      this.cityToggle.setAttribute('aria-checked', String(this.showCities));
    }

    scrollIntoWorkspace() {
      const workspace = this.querySelector('.wine-atlas__workspace');
      workspace?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    updateUrl() {
      try {
        const url = new URL(window.location.href);
        if (this.selectedId) url.searchParams.set('atlas-region', this.selectedId);
        else url.searchParams.delete('atlas-region');
        if (this.mode === 'all') url.searchParams.set('atlas-mode', 'all');
        else url.searchParams.delete('atlas-mode');
        if (this.search?.value.trim()) url.searchParams.set('atlas-q', this.search.value.trim());
        else url.searchParams.delete('atlas-q');
        if (this.useFixedLevels) {
          url.searchParams.set('level', this.mapLevel);
          ['atlas-zoom', 'atlas-scale', 'zoom', 'scale'].forEach((parameter) => url.searchParams.delete(parameter));
        } else {
          url.searchParams.delete('level');
        }
        window.history.replaceState({}, '', url);
      } catch (error) {
        // URL state is a progressive enhancement.
      }
    }

    async copyView(button) {
      this.updateUrl();
      const originalText = button.textContent;
      try {
        await navigator.clipboard.writeText(window.location.href);
        button.textContent = this.t('view_copied');
      } catch (error) {
        button.textContent = this.t('view_updated');
      }
      window.setTimeout(() => {
        button.textContent = originalText;
      }, 1800);
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase(this.dataset.locale || 'de');
    }

    safeUrl(value) {
      const url = String(value || '');
      if (/^(https?:\/\/|\/|#)/i.test(url)) return url;
      return '#';
    }

    escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    escapeAttribute(value) {
      return this.escape(value).replace(/`/g, '&#096;');
    }
  }

  customElements.define('wine-atlas', WineAtlas);
})();
